import { useEffect, useState } from 'react'
import { Bluetooth, Check, Loader2, Plus, RefreshCw, Usb, X } from 'lucide-react'
import { AppButton } from '@/components/rfid/AppButton'
import { BRAND } from '@/constants/brand'
import { SCANNER_SETUP_GUIDES } from '@/constants/scannerSetup'
import {
  addManualScanner,
  authorizeNearbyRfidScanner,
  isWebBluetoothAvailable,
  pickHidScanner,
  refreshAvailableScanners,
  type KnownScannerDevice,
} from '@/services/scannerDevices'
import type { ScannerType } from '@/stores/rfidSessionStore'
import { cn } from '@/utils/cn'

export type ScannerDevicePickerModalProps = {
  open: boolean
  type: ScannerType
  /** Currently selected machine (pending or live). */
  selectedName?: string | null
  /** True only after a physical tag verified the link. */
  verified?: boolean
  busy?: boolean
  onClose: () => void
  onConnect: (device: KnownScannerDevice) => void | Promise<void>
}

export function ScannerDevicePickerModal({
  open,
  type,
  selectedName,
  verified,
  busy,
  onClose,
  onConnect,
}: ScannerDevicePickerModalProps) {
  const guide = SCANNER_SETUP_GUIDES[type]
  const [devices, setDevices] = useState<KnownScannerDevice[]>([])
  const [loading, setLoading] = useState(false)
  const [actionBusy, setActionBusy] = useState(false)
  const [manualCode, setManualCode] = useState('')
  const [showManual, setShowManual] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const reload = async () => {
    setLoading(true)
    setError(null)
    try {
      const list = await refreshAvailableScanners(type)
      setDevices(list)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not refresh scanners')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (!open) return
    setManualCode('')
    setShowManual(false)
    setError(null)
    void reload()
  }, [open, type])

  if (!open) return null

  const run = async (fn: () => Promise<void>) => {
    setActionBusy(true)
    setError(null)
    try {
      await fn()
      await reload()
    } catch (e) {
      if (e instanceof DOMException && e.name === 'NotFoundError') return
      setError(e instanceof Error ? e.message : 'Action failed')
    } finally {
      setActionBusy(false)
    }
  }

  const disabled = Boolean(busy || actionBusy || loading)

  return (
    <div
      className="fixed inset-0 z-[1000] flex items-end justify-center bg-black/45 p-3 sm:items-center"
      role="dialog"
      aria-modal="true"
      aria-labelledby="scanner-picker-title"
      onClick={onClose}
    >
      <div
        className="flex max-h-[min(92vh,720px)] w-full max-w-lg flex-col overflow-hidden rounded-2xl border border-border bg-card shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div
          className="flex items-start gap-3 border-b border-border px-4 py-3.5"
          style={{ background: 'var(--leap-blush)' }}
        >
          <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-xl bg-white/80">
            <img
              src={guide.imageSrc}
              alt={guide.title}
              className="max-h-12 max-w-12 object-contain"
            />
          </div>
          <div className="min-w-0 flex-1">
            <div
              id="scanner-picker-title"
              className="text-[15px] font-black text-foreground"
            >
              Select {type}
            </div>
            <p className="m-0 mt-0.5 text-[11.5px] font-semibold leading-4 text-muted-foreground">
              Choose an RFID machine code below. Only RFD scanners are listed — not other
              Bluetooth devices.
            </p>
          </div>
          <button
            type="button"
            className="inline-flex h-8 w-8 items-center justify-center rounded-lg text-muted-foreground hover:bg-black/5"
            aria-label="Close"
            onClick={onClose}
          >
            <X size={18} />
          </button>
        </div>

        <div className="flex flex-wrap gap-2 border-b border-border px-4 py-2.5">
          <button
            type="button"
            disabled={disabled}
            onClick={() => void reload()}
            className="inline-flex items-center gap-1.5 rounded-lg border border-border bg-muted/40 px-2.5 py-1.5 text-[11px] font-extrabold disabled:opacity-50"
          >
            <RefreshCw size={13} className={loading ? 'animate-spin' : undefined} />
            Refresh list
          </button>
          <button
            type="button"
            disabled={disabled || !isWebBluetoothAvailable()}
            onClick={() =>
              void run(async () => {
                await authorizeNearbyRfidScanner(type)
              })
            }
            className="inline-flex items-center gap-1.5 rounded-lg border border-border bg-muted/40 px-2.5 py-1.5 text-[11px] font-extrabold disabled:opacity-50"
            title="One-time browser permission — filtered to RFD names only"
          >
            <Bluetooth size={13} />
            Authorize nearby {type}
          </button>
          <button
            type="button"
            disabled={disabled}
            onClick={() =>
              void run(async () => {
                await pickHidScanner(type)
              })
            }
            className="inline-flex items-center gap-1.5 rounded-lg border border-border bg-muted/40 px-2.5 py-1.5 text-[11px] font-extrabold disabled:opacity-50"
          >
            <Usb size={13} />
            Authorize USB
          </button>
          <button
            type="button"
            disabled={disabled}
            onClick={() => setShowManual((v) => !v)}
            className="inline-flex items-center gap-1.5 rounded-lg border border-border bg-muted/40 px-2.5 py-1.5 text-[11px] font-extrabold disabled:opacity-50"
          >
            <Plus size={13} />
            Enter machine code
          </button>
        </div>

        {showManual ? (
          <div className="flex gap-2 border-b border-border px-4 py-2.5">
            <input
              className="rfid-field__control min-w-0 flex-1 font-mono text-[12px]"
              placeholder={
                type === 'RFD8500' ? 'RFD850017346523021605' : 'RFD40…'
              }
              value={manualCode}
              autoFocus
              data-no-wedge="true"
              onChange={(e) => setManualCode(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  void run(async () => {
                    addManualScanner(type, manualCode)
                    setManualCode('')
                    setShowManual(false)
                  })
                }
              }}
            />
            <AppButton
              disabled={disabled}
              onClick={() =>
                void run(async () => {
                  addManualScanner(type, manualCode)
                  setManualCode('')
                  setShowManual(false)
                })
              }
            >
              Add
            </AppButton>
          </div>
        ) : null}

        {error ? (
          <div className="mx-4 mt-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-[11.5px] font-semibold text-red-700">
            {error}
          </div>
        ) : null}

        <div className="min-h-0 flex-1 overflow-y-auto px-4 py-3">
          {loading && devices.length === 0 ? (
            <div className="flex items-center justify-center gap-2 py-10 text-[12px] font-semibold text-muted-foreground">
              <Loader2 size={16} className="animate-spin" />
              Looking for {type} scanners…
            </div>
          ) : devices.length === 0 ? (
            <div className="rounded-[12px] border border-dashed border-border bg-muted/40 px-4 py-8 text-center">
              <div className="text-[13px] font-extrabold text-foreground">
                No {type} machine codes yet
              </div>
              <p className="m-0 mt-2 text-[12px] leading-[17px] text-muted-foreground">
                Pair the sled in your OS Bluetooth settings (it must appear as a keyboard for
                web scanning), then tap <strong>Enter machine code</strong> and paste the exact
                name (e.g. RFD850017346523021605). Or use <strong>Authorize nearby {type}</strong>{' '}
                if the browser can see it as BLE.
              </p>
            </div>
          ) : (
            <ul className="m-0 space-y-2 p-0">
              {devices.map((device) => {
                const isSelected = selectedName === device.name
                const isLive = isSelected && verified
                return (
                  <li key={device.id}>
                    <button
                      type="button"
                      disabled={disabled}
                      onClick={() => void onConnect(device)}
                      className={cn(
                        'flex w-full items-center gap-3 rounded-[12px] border px-3.5 py-3 text-left transition-colors',
                        isLive
                          ? 'border-emerald-500 bg-emerald-50'
                          : isSelected
                            ? 'border-amber-400 bg-amber-50'
                            : 'border-border bg-card hover:border-[var(--leap-brand)]/45 hover:bg-[rgba(175, 0, 0,0.03)]'
                      )}
                    >
                      <span
                        className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg"
                        style={{ background: `${BRAND}12`, color: BRAND }}
                      >
                        <Bluetooth size={16} />
                      </span>
                      <span className="min-w-0 flex-1">
                        <code className="block truncate font-mono text-[13px] font-bold text-foreground">
                          {device.name}
                        </code>
                        <span className="mt-0.5 block text-[10px] font-extrabold uppercase tracking-wide text-muted-foreground">
                          {isLive
                            ? 'Connected · verified by tag'
                            : isSelected
                              ? 'Selected · waiting for trigger'
                              : `${type} · ${device.source} · tap to select`}
                        </span>
                      </span>
                      {isLive ? (
                        <Check size={16} className="shrink-0 text-emerald-600" strokeWidth={3} />
                      ) : null}
                    </button>
                  </li>
                )
              })}
            </ul>
          )}
        </div>

        <div className="border-t border-border px-4 py-3">
          <AppButton variant="outline" block onClick={onClose} disabled={actionBusy}>
            Close
          </AppButton>
        </div>
      </div>
    </div>
  )
}
